import React from 'react';
import Fader from 'react-fader';
import { Route, Redirect } from 'react-router-dom';
import Switch from 'react-router-transition-switch';

import ApplicationLayout from 'components/ApplicationLayout';
import useIsMobile from 'hooks/useIsMobile';
import Booking from 'screens/components/Booking';
import CustomerSupport from 'screens/components/CustomerSupport';
import GuestNotAuthorized from 'screens/components/Login/forms/GuestNotAuthorized';
import Root from 'screens/components/Root';
import TravelRequested from 'screens/components/TravelRequested';
import AutosList from 'screens/containers/AutosList';
import CostCenterReportContainer from 'screens/containers/CostCenterReportContainer';
import Dashboard from 'screens/containers/Dashboard';
import Expense from 'screens/containers/Expense';
import ExpensesList from 'screens/containers/ExpensesList';
import ExpensesListImport from 'screens/containers/ExpensesListImport';
import FlightsList from 'screens/containers/FlightsList';
import HotelsList from 'screens/containers/HotelsList';
import InviteContainer from 'screens/containers/Invite';
import Login from 'screens/containers/Login';
import LoginEmail from 'screens/containers/LoginEmail';
import OAuthDefaultContainer from 'screens/containers/OAuthDefaultContainer';
import RegistrationOnboardingContainer from 'screens/containers/RegistrationOnboarding';
import RequestHistory from 'screens/containers/RequestHistory';
import TravelerProfile from 'screens/containers/TravelerProfile';
import TravelerProfileEdit from 'screens/containers/TravelerProfileEdit';
import TravelerTripPreferences from 'screens/containers/TravelerTripPreferences';
import TravelsWithExpensesList from 'screens/containers/TravelsWithExpensesList';
import UserCountrySelectionContainer from 'screens/containers/UserCountrySelection';
import UsersListContainer from 'screens/containers/Users';
import UsersForm from 'screens/containers/UsersForm';

interface Props {
  component: React.ComponentType<any>;
  exact?: boolean;
  path: string;
  isGroupApprover: boolean;
}

const PrivateRoute = ({ component: Component, isGroupApprover, ...props }: Props) => (
  <Route
    {...props}
    render={(props) =>
      isGroupApprover ? (
        <Component {...props} />
      ) : (
        <Redirect
          to={{
            pathname: '/booking',
            state: { from: props.location },
          }}
        />
      )
    }
  />
);

interface TProps {
  isGroupApprover: boolean;
  isTravelArranger: boolean;
}

const TravelerRouter = ({ isGroupApprover, isTravelArranger }: TProps) => {
  const isMobile = useIsMobile();

  return (
    <Switch
      render={({ children }) => (
        <ApplicationLayout>
          <Fader viewStyle={isMobile ? { height: '100vh' } : {}}>{children}</Fader>
        </ApplicationLayout>
      )}
    >
      <Route path="/invite" component={InviteContainer} />
      <Route path="/registration" component={RegistrationOnboardingContainer} />
      <Route exact path="/login-email" component={LoginEmail} />
      <Route path="/login" component={Login} />
      <Route path="/customerSupport" component={CustomerSupport} />
      <Route path="/travelRequested" component={TravelRequested} />
      <Route path="/booking" component={Booking} />
      <Route exact path="/dashboard" component={Dashboard} />
      <Route exact path="/dashboard/v2" component={Dashboard} />
      <Route path="/flights" component={FlightsList} />
      <Route path="/hotels" component={HotelsList} />
      <Route path="/autos" component={AutosList} />
      <Route exact path="/travelsWithExpenses" component={TravelsWithExpensesList} />
      <Route exact path="/expense" component={Expense} />
      <Route exact path="/expense/:expenseId" component={Expense} />
      <Route exact path="/expenses/:travelId" component={ExpensesList} />
      <Route exact path="/expensesImport/:travelId" component={ExpensesListImport} />
      <PrivateRoute isGroupApprover={isGroupApprover} exact path="/requestHistory" component={RequestHistory} />
      <Route exact path="/travelerProfile" component={TravelerProfile} />
      <Route exact path="/travelerProfileEdit" component={TravelerProfileEdit} />
      <Route exact path="/travelerProfile/preferences/trip" component={TravelerTripPreferences} />
      <Route exact path="/oauth-login" component={OAuthDefaultContainer} />
      <Route exact path="/oauth-registration" component={OAuthDefaultContainer} />
      <Route exact path="/oauth-invite" component={OAuthDefaultContainer} />
      <Route exact path="/costCenterReport" component={CostCenterReportContainer} />
      <Route exact path="/login-not-authorized" component={GuestNotAuthorized} />
      <Route exact path="/user-country-selection" component={UserCountrySelectionContainer} />

      {isTravelArranger && (
        <>
          <Route exact path="/usersList" component={UsersListContainer} />
          <Route exact path="/usersForm" component={UsersForm} />
          <Route exact path="/usersForm/:userId" component={UsersForm} />
        </>
      )}

      <Route exact path="/" component={Root} />
    </Switch>
  );
};

export default TravelerRouter;
